import {
  Box,
  Button,
  Checkbox,
  Dialog,
  Divider,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AttachMoney from "@mui/icons-material/AttachMoney";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import {
  AccountBalance,
  CreditCard,
  Payments,
  InfoOutlined,
  SearchOutlined,
} from "@mui/icons-material";
import { Receipt } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import CustomStyledRadio from "../ivr/CustomStyledRadio";
import CustomCurrencyField from "../../common/CustomCurrencyField";
// CS-1765 - Import renderDynamicAgingColumn for INV Aging column
import { priceFormatter, renderDynamicAgingColumn } from "../../../utils/Utility";
import { useModalStyles } from "../../common/useModalStyles";
import CustomCheckbox from "../../common/CustomCheckBox";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import Table from "../../common/CustomTable";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { fetchBulkPaymentInvoiceList, SearchInvoiceInfo, useInvoiceSlice, clearBulkPaymentInvoiceList, bulkPaymentCheckoutData, invoiceInfo, fetchBulkPaymentProducts, clearBulkPaymentProducts } from "../../../redux/slice/invoice/InvoiceSlice";
import { setSingleAlertObj, SingleAlertInfo } from "../../../redux/slice/commonSlice";
import { useLocation } from "react-router-dom";

interface BulkInvoicePaymentProps {
  open: boolean;
  onClose: () => void;
  createCheckoutSession: (
    invoiceId: number,
    paymentMethodType: string,
    paymentType: string,
    paymentAmount: number
  ) => void;
  onResetSelection?: () => void; // Callback to reset InvoiceList selections
  practiceOptions?: { label: string; value: any }[];

  stripeConfig?: any; // Stripe configuration for payment methods
}

interface Column {
  id: string;
  label: string | JSX.Element;
  minWidth?: number;
  align?: "right" | "center" | "left";
  padding?: string;
}

interface InvoiceTableRow {
  invoiceId: number;
  mfgInvoiceNumber: string;
  checkBox: JSX.Element;
  mfgInvoiceDate: string;
  invoiceStatus: string;
  invoiceDueDate: string;
  paymentType: JSX.Element;
  outstandingAmount: string;
  paymentAmount: JSX.Element;
  actualRemainingAmount: number;
  // CS-1765 - INV Aging column
  invoiceLateDays?: JSX.Element | string;
  // Raw sortable values (CustomTable looks up raw<ColumnId> for client-side sorting)
  rawMfgInvoiceNumber?: string;
  rawMfgInvoiceDate?: number;
  rawInvoiceDueDate?: number;
  rawInvoiceLateDays?: number;
  rawOutstandingAmount?: number;
  rawInvoiceStatus?: string;
  rawPaymentType?: string;
  rawPaymentAmount?: number;
}

const formatMethodLabel = (code: string) => {
  let s = code.trim().replace(/_/g, " ");
  s = s.replace(/\bUs\b/g, "US");
  return s;
};

const resolvePaymentIcon = (code: string): JSX.Element => {
  const c = code.toUpperCase();
  if (c.includes("CARD") || c.includes("CREDIT") || c.includes("DEBIT"))
    return <CreditCard />;
  if (c.includes("BANK") || c.includes("ACH") || c.includes("US_BANK_ACCOUNT"))
    return <AccountBalance />;
  return <Payments />;
};

const getPaymentMethodUI = (code: string) => ({
  label: code === "US_BANK_ACCOUNT" ? "ACH" : formatMethodLabel(code),
  leftIcon: resolvePaymentIcon(code),
});

type FeeKeys = "percent" | "additional" | "max_charge" | "min_charge";
type FeeConfig = Partial<Record<FeeKeys, number>>;

const parseNumber = (val: string) => {
  const n = Number(String(val).replace(/[%$,\s]/g, ""));
  return Number.isFinite(n) ? n : undefined;
};

const FEE_PREFIX = "platform_fee_";

const getMethodFeesDynamic = (
  stripeConfig: any,
  methodCode: string
): FeeConfig => {
  const out: FeeConfig = {};
  if (!stripeConfig || stripeConfig.platform_fee_applicable !== "Y") {
    return out;
  }

  const methodKey = methodCode.toLowerCase();
  const prefix = `${FEE_PREFIX}${methodKey}_`;

  for (const key of Object.keys(stripeConfig)) {
    if (!key.toLowerCase().startsWith(prefix)) continue;

    const tail = key.substring(prefix.length);

    const num = parseNumber(stripeConfig[key]);
    if (num !== undefined) {
      out[tail as FeeKeys] = num;
    }
  }

  return out;
};

const isPositive = (n?: number) => typeof n === "number" && n > 0;

const STRIPE_MAX_AMOUNT = 999999.99;

const SORTABLE_INVOICE_COLUMNS = [
  "mfgInvoiceNumber",
  "mfgInvoiceDate",
  "invoiceDueDate",
  "invoiceLateDays",
  "outstandingAmount",
  "invoiceStatus",
  "paymentType",
  "paymentAmount",
];

const BulkInvoicePayment: React.FC<BulkInvoicePaymentProps> = ({
  open,
  onClose,
  createCheckoutSession,
  onResetSelection,
  practiceOptions = [],
  stripeConfig: propStripeConfig,
}) => {
  const {
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    clearErrors,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
  });


  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDark = theme.palette.mode === "dark";
  const {
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    bodySx,
    footerSx,
    closeIconSx,
    dropdownWrapWithLabelSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { bulkPaymentInvoiceList, bulkPaymentProducts, bulkPaymentProductsFetchStatus } = useInvoiceSlice();
  
  // Flag to track if initial search has been triggered
  const [hasInitialSearchTriggered, setHasInitialSearchTriggered] = useState(false);

  const lastRequestedPracticeIdRef = useRef<any>(undefined);
  const selectedPracticeId = useWatch({ control, name: "practiceId" });

  // CS-1995 - Product (MFR) dropdown options derived from the slice (products with open PARTIAL/UNPAID invoices)
  const productOptionsForPractice = useMemo(
    () =>
      (bulkPaymentProducts ?? []).map((product: any) => ({
        label: `${product.productName} (${product.manufacturerName || "Unknown MFR"})`,
        value: Number(product.productId),
      })),
    [bulkPaymentProducts]
  );

  const loadProductsForPractice = (practiceId: any) => {
    if (lastRequestedPracticeIdRef.current === practiceId) {
      return;
    }
    lastRequestedPracticeIdRef.current = practiceId;

    setValue("productId", undefined);
    dispatch(clearBulkPaymentInvoiceList());

    if (!practiceId) {
      dispatch(clearBulkPaymentProducts());
      return;
    }

    dispatch(fetchBulkPaymentProducts(Number(practiceId)));
  };

  // CS-1995 - clear bulk payment product state when the dialog unmounts so it does not persist in the slice
  useEffect(() => {
    return () => {
      dispatch(clearBulkPaymentProducts());
    };
  }, [dispatch]);


  // Get stripe config from prop or first invoice in the list
  const stripeConfig = propStripeConfig || bulkPaymentInvoiceList?.[0]?.stripeConfig;
  const methodCfg = stripeConfig?.payment_method || "";

  useEffect(() => {
    if (practiceOptions?.length === 1 && !watch("practiceId")) {
      const onlyPracticeId = practiceOptions[0].value;
      setValue("practiceId", onlyPracticeId);
      loadProductsForPractice(onlyPracticeId);
    }
  }, [practiceOptions, setValue, watch]);

  // CS-1995 - Auto-select the only product once its options have been fetched
  useEffect(() => {
    if (productOptionsForPractice?.length === 1 && !watch("productId")) {
      setValue("productId", productOptionsForPractice[0].value);
    }
  }, [productOptionsForPractice, setValue, watch]);

  // CS-1765 - Handle search using productId instead of mfgId
  const handleSearch = async () => {
    const practiceId = watch("practiceId");
    const productId = watch("productId");

    // Validation: Both fields must be selected
    if (!practiceId || !productId) {
      const alertObj: SingleAlertInfo = {
        message: "Please select both Product and Practice to search invoices.",
        alertType: "error",
      };
      dispatch(setSingleAlertObj(alertObj));
      return;
    }

    // Clear Redux state first
    dispatch(clearBulkPaymentInvoiceList());

    // Clear all invoice-related form state
    const currentValues = watch();
    
    // Clear all invoice checkbox, payment type, and payment amount fields
    Object.keys(currentValues).forEach((key) => {
      if (key.startsWith('invoiceCheckbox.') || 
          key.startsWith('paymentType.') || 
          key.startsWith('paymentAmount.')) {
        setValue(key as any, undefined);
      }
    });
    
    setValue("selectAllCheckBox", false);

    const searchParams: SearchInvoiceInfo = {
      fromDate: undefined,
      toDate: undefined,
      agencyIds: [],
      patientIds: [],
      mfgInvoiceNumber: null,
      orderNumber: null,
      mfgIds: [],
      practiceIds: [practiceId],
      invoiceStatus: ["PARTIAL", "UNPAID","PROCESSING"],
      currentPage: 1,
      itemsPerPage: 99,
      isDownload: false,
      excludePurchaseAndBill: false,
      // CS-1765 - Pass productId for product-based invoice search
      productId: productId,
    };

    try {
      const response = await dispatch(fetchBulkPaymentInvoiceList(searchParams));
      
      if (response.payload && response.payload?.data?.content && response.payload?.data?.content?.length > 0) {
        const firstInvoice = response.payload.data.content[0];
        if (firstInvoice.stripeConfig && firstInvoice.stripeConfig.stripe_integration !== 'Y') {
          const alertObj: SingleAlertInfo = {
            message: "Bulk payment is not available. Stripe integration is not enabled for the selected Manufacturer.",
            alertType: "error",
          };
          dispatch(clearBulkPaymentInvoiceList());
          dispatch(setSingleAlertObj(alertObj));
          return;
        }
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
    }
  };

  // Payment method options
  const paymentOptions = useMemo(() => {
    if (!methodCfg) return [];
    const options = methodCfg
      .split(",")
      .map((s: string) => s.trim())
      .filter(Boolean)
      .map((code: string) => {
        const ui = getPaymentMethodUI(code);
        return { value: code, label: ui.label, leftIcon: ui.leftIcon };
      });

    // Sort to show US_BANK_ACCOUNT (ACH) first
    return options.sort((a: any, b: any) => {
      if (a.value === "US_BANK_ACCOUNT") return -1;
      if (b.value === "US_BANK_ACCOUNT") return 1;
      return 0;
    });
  }, [methodCfg]);

  // Payment type options
  const paymentTypeOptions = useMemo(() => [
    { label: "FULL", value: "FULL" },
    { label: "PARTIAL", value: "PARTIAL" },
  ], []);

  // Initialize form values when bulk payment invoice list changes
  useEffect(() => {
    if (!bulkPaymentInvoiceList || bulkPaymentInvoiceList.length === 0) return;

    // Initialize payment method
    if (paymentOptions.length > 0 && !watch("paymentMethodType")) {
      setValue("paymentMethodType", paymentOptions[0].value, {
        shouldValidate: true,
      });
    }

    // Initialize all invoices with FULL payment type and checkbox checked
    bulkPaymentInvoiceList.forEach((invoice: invoiceInfo) => {
      const outstandingAmount = invoice.remainingAmount || invoice.outstandingAmount || 0;
      const orderIdNull = invoice.orderId === null || invoice.orderId === undefined;
      if (Number(outstandingAmount) > 0 && !orderIdNull) {
        if (watch(`invoiceCheckbox.${invoice.invoiceId}`) === undefined) {
          setValue(`invoiceCheckbox.${invoice.invoiceId}`, true, {
            shouldValidate: true,
            shouldDirty: false,
          });
        }
        
        if (!watch(`paymentType.${invoice.invoiceId}`)) {
          setValue(`paymentType.${invoice.invoiceId}`, "FULL", {
            shouldValidate: true,
            shouldDirty: false,
          });
        }
        
        if (!watch(`paymentAmount.${invoice.invoiceId}`)) {
          setValue(`paymentAmount.${invoice.invoiceId}`, Number(outstandingAmount), {
            shouldValidate: true,
            shouldDirty: false,
          });
        }
      } else {
        setValue(`invoiceCheckbox.${invoice.invoiceId}`, false, {
          shouldValidate: false,
          shouldDirty: false,
        });
      }
    });
  }, [bulkPaymentInvoiceList, paymentOptions, setValue, watch]);

  const selectedMethod = watch("paymentMethodType");
  
  const invoiceCheckboxes = useWatch({ 
    control, 
    name: "invoiceCheckbox"
  });
  const paymentTypes = useWatch({ 
    control, 
    name: "paymentType"
  });
  const paymentAmounts = useWatch({ 
    control, 
    name: "paymentAmount"
  });

  const selectedFees = useMemo(
    () => getMethodFeesDynamic(stripeConfig, selectedMethod || ""),
    [stripeConfig, selectedMethod]
  );

  const activeInvoices = useMemo(() => {
    if (!bulkPaymentInvoiceList || bulkPaymentInvoiceList.length === 0) return [];
    
    const active = bulkPaymentInvoiceList.filter((invoice: invoiceInfo) => {
      const isChecked = invoiceCheckboxes?.[invoice.invoiceId] === true;
      return isChecked;
    });
    console.log('Active invoices recalculated:', active.length, 'Total invoices:', bulkPaymentInvoiceList.length);
    console.log('Checkbox state:', invoiceCheckboxes);
    return active;
  }, [bulkPaymentInvoiceList, invoiceCheckboxes]);

  // Calculate totals
  const calculations = useMemo(() => {
    const numberOfInvoices = activeInvoices.length;
    let totalInvoiceAmount = 0;
    let totalPaymentAmount = 0;

    activeInvoices.forEach((invoice: invoiceInfo) => {
      const outstandingAmount =
        invoice.remainingAmount || invoice.outstandingAmount || 0;
      totalInvoiceAmount += Number(outstandingAmount);

      const paymentAmount = Number(
        paymentAmounts?.[invoice.invoiceId] || outstandingAmount
      );
      totalPaymentAmount += paymentAmount;
    });

    const percentFee = selectedFees.percent ?? 0;
    const additionalFee = selectedFees.additional ?? 0;
    const maxCharge = selectedFees.max_charge ?? 0;
    const minCharge = selectedFees.min_charge ?? 0;
    const isACH = selectedMethod === "US_BANK_ACCOUNT";

    let totalFees = 0;

    if (totalPaymentAmount > 0) {
      const percentageFeeDecimal = percentFee / 100;
      const fixedFeeDollars = additionalFee;

      if (isPositive(percentFee) || isPositive(additionalFee)) {
        totalFees =
          (totalPaymentAmount + fixedFeeDollars) / (1 - percentageFeeDecimal) -
          totalPaymentAmount;
      }

      if (isPositive(maxCharge) && totalFees > maxCharge) {
        totalFees = maxCharge;
      }

      if (isPositive(minCharge) && totalFees < minCharge) {
        totalFees = minCharge;
      }

      if (isACH && isPositive(percentFee)) {
        const achPercentageFee = (totalPaymentAmount * percentFee) / 100;
        const achFeeAmount = achPercentageFee >= 5 ? 5 : achPercentageFee;
        totalFees = achFeeAmount;
      }
    }

    const expectedPaymentAmount = totalPaymentAmount + totalFees;
    const remainingBalance = totalInvoiceAmount - totalPaymentAmount;

    console.log('Calculations updated:', {
      numberOfInvoices,
      totalInvoiceAmount,
      totalPaymentAmount,
      totalFees,
      expectedPaymentAmount
    });

    return {
      numberOfInvoices,
      totalInvoiceAmount,
      totalPaymentAmount,
      totalFees,
      expectedPaymentAmount,
      remainingBalance,
    };
  }, [
    activeInvoices,
    paymentAmounts,
    selectedFees,
    selectedMethod,
  ]);

  // Fee text
  const feeText = useMemo(() => {
    const parts: string[] = [];
    const isACH = selectedMethod === "US_BANK_ACCOUNT";

    if (isPositive(selectedFees.percent))
      parts.push(`${selectedFees.percent}%`);
    if (isPositive(selectedFees.additional))
      parts.push(`${priceFormatter(Number(selectedFees.additional))}`);

    const main = parts.join(" + ");

    if (!main) return "";

    if (isACH && isPositive(selectedFees.percent)) {
      return `Estimated Stripe Processing Fees: ${
        selectedFees.percent
      }% capped at ${priceFormatter(5)}`;
    }

    return `Estimated Stripe Processing Fees: ${main}`;
  }, [selectedFees, selectedMethod]);

  // Validation
  const isAmountExceeded = calculations.expectedPaymentAmount > STRIPE_MAX_AMOUNT;
  const hasNoActiveInvoices = calculations.numberOfInvoices === 0;
  const isTotalAmountTooLow = calculations.totalPaymentAmount > 0 && calculations.totalPaymentAmount < 0.5;
  const hasInvalidAmounts = useMemo(() => {
    return activeInvoices.some((invoice: invoiceInfo) => {
      const paymentAmount = Number(paymentAmounts?.[invoice.invoiceId] || 0);
      const outstandingAmount =
        invoice.remainingAmount || invoice.outstandingAmount || 0;
      const paymentType = paymentTypes?.[invoice.invoiceId];
      
      if (!paymentType) {
        return true;
      }
      
      if (paymentType === "PARTIAL") {
        if (!paymentAmount || paymentAmount === 0) {
          return true;
        }
        
        if (paymentAmount === Number(outstandingAmount)) {
          return true;
        }
        
        if (paymentAmount > Number(outstandingAmount)) {
          return true;
        }
      }
      
      if (paymentType === "FULL") {
        if (paymentAmount !== Number(outstandingAmount)) {
          return true;
        }
      }
      
      if (isNaN(paymentAmount)) {
        return true;
      }
      
      return false;
    });
  }, [activeInvoices, paymentAmounts, paymentTypes]);

  const isSubmitDisabled =
    isAmountExceeded ||
    hasNoActiveInvoices ||
    calculations.numberOfInvoices < 2 ||
    isTotalAmountTooLow ||
    hasInvalidAmounts ||
    paymentOptions.length === 0;

  // Format rows for CustomTable
  const formattedInvoiceRows: InvoiceTableRow[] = useMemo(() => {
    if (!bulkPaymentInvoiceList || bulkPaymentInvoiceList.length === 0) return [];

    const sortedByAging = [...bulkPaymentInvoiceList].sort((a, b) => {
      const dueDateA = a.invoiceDueDate ? new Date(a.invoiceDueDate).getTime() : Infinity;
      const dueDateB = b.invoiceDueDate ? new Date(b.invoiceDueDate).getTime() : Infinity;
      return dueDateA - dueDateB;
    });

    return sortedByAging.map((invoice: invoiceInfo) => {
      const outstandingAmount = invoice.remainingAmount || invoice.outstandingAmount || 0;
      const paymentType = paymentTypes?.[invoice.invoiceId] || "FULL";
      const isChecked = invoiceCheckboxes?.[invoice.invoiceId] === true;
      const isZeroAmount = Number(outstandingAmount) <= 0;
      const isOrdeIdNull = invoice.orderId === null || invoice.orderId === undefined;

      return {
        invoiceId: invoice.invoiceId,
        mfgInvoiceNumber: invoice.mfgInvoiceNumber || `INV-${invoice.invoiceId}`,
        rawMfgInvoiceNumber: invoice.mfgInvoiceNumber || `INV-${invoice.invoiceId}`,
        actualRemainingAmount: Number(outstandingAmount),
        mfgInvoiceDate: invoice.mfgInvoiceDate || "-",
        rawMfgInvoiceDate: invoice.mfgInvoiceDate ? new Date(invoice.mfgInvoiceDate).getTime() : 0,
        invoiceStatus: invoice.invoiceStatus || "-",
        rawInvoiceStatus: invoice.invoiceStatus || "",
        invoiceDueDate: invoice.invoiceDueDate || "-",
        rawInvoiceDueDate: invoice.invoiceDueDate ? new Date(invoice.invoiceDueDate).getTime() : 0,
        // CS-1765 - INV Aging column using shared renderDynamicAgingColumn
        invoiceLateDays: renderDynamicAgingColumn(invoice),
        rawInvoiceLateDays: Number(invoice.invoiceLateDays ?? 0),
        checkBox: (
          <CustomCheckbox
            control={control}
            controllerName={`invoiceCheckbox.${invoice.invoiceId}`}
            isDisabled={isZeroAmount || isOrdeIdNull}
          />
        ),
        paymentType: (
          <CustomDropdownField
            control={control}
            name={`paymentType.${invoice.invoiceId}`}
            options={paymentTypeOptions}
            disabled={!isChecked || isZeroAmount || isOrdeIdNull}
            size="small"
            rules={{
              required: isChecked && !isZeroAmount && !isOrdeIdNull ? "Payment type is required" : false,
            }}
            onChangeSelect={(value: any) => handlePaymentTypeChange(invoice.invoiceId, value)}
          />
        ),
        rawPaymentType: paymentType,
        outstandingAmount: priceFormatter(Number(outstandingAmount)),
        rawOutstandingAmount: Number(outstandingAmount),
        paymentAmount: (
          <CustomCurrencyField
            control={control}
            controllerName={`paymentAmount.${invoice.invoiceId}`}
            isDisabled={!isChecked || paymentType === "FULL" || isZeroAmount || isOrdeIdNull}
            size="small"
            rules={{
              validate: (value: any) => {
                if (!isChecked || isZeroAmount || isOrdeIdNull) return true;

                const num = Number(value);

                if (paymentType === "PARTIAL") {
                  if (!value || value === "" || value === null || value === undefined) {
                    return "Payment amount is required";
                  }

                  if (isNaN(num)) {
                    return "Invalid amount";
                  }

                  if (num <= 0) {
                    return "Amount must be greater than $0";
                  }

                  if (num === Number(outstandingAmount)) {
                    return "Entered amount matches outstanding.Use FULL option.";
                  }

                  if (num > Number(outstandingAmount)) {
                    return `Amount cannot exceed ${priceFormatter(Number(outstandingAmount))}`;
                  }
                }

                if (paymentType === "FULL") {
                  if (num !== Number(outstandingAmount)) {
                    return "Amount must be greater than $0";
                  }
                }

                return true;
              },
            }}
          />
        ),
        rawPaymentAmount: Number(paymentAmounts?.[invoice.invoiceId] ?? outstandingAmount),
      };
    });
  }, [
    bulkPaymentInvoiceList,
    paymentTypes,
    paymentAmounts,
    invoiceCheckboxes,
    control,
    setValue,
    paymentTypeOptions,
    clearErrors,
  ]);

  // Handle select all checkbox
  const handleSelectAllCheckBox = (checked: boolean) => {
    if (!bulkPaymentInvoiceList || bulkPaymentInvoiceList.length === 0) return;
    
    bulkPaymentInvoiceList.forEach((invoice: invoiceInfo) => {
      const outstandingAmount = invoice.remainingAmount || invoice.outstandingAmount || 0;
      const orderIdNull = invoice.orderId === null || invoice.orderId === undefined;
      if (Number(outstandingAmount) > 0 && !orderIdNull) {
        setValue(`invoiceCheckbox.${invoice.invoiceId}`, checked);
      }
    });
  };

  const allInvoicesSelected = useMemo(() => {
    if (!bulkPaymentInvoiceList || bulkPaymentInvoiceList.length === 0) return false;
    
    const selectableInvoices = bulkPaymentInvoiceList.filter((invoice: invoiceInfo) => {
      const outstandingAmount = invoice.remainingAmount || invoice.outstandingAmount || 0;
      return Number(outstandingAmount) > 0;
    });
    
    if (selectableInvoices.length === 0) return false;
    
    return selectableInvoices.every((invoice: invoiceInfo) => 
      invoiceCheckboxes?.[invoice.invoiceId] === true
    );
  }, [bulkPaymentInvoiceList, invoiceCheckboxes]);

  useEffect(() => {
    setValue("selectAllCheckBox", allInvoicesSelected);
  }, [allInvoicesSelected, setValue]);

  // Handle payment type changes
  useEffect(() => {
    if (!bulkPaymentInvoiceList || bulkPaymentInvoiceList.length === 0) return;

    bulkPaymentInvoiceList.forEach((invoice: invoiceInfo) => {
      const paymentType = paymentTypes?.[invoice.invoiceId];
      const outstandingAmount = invoice.remainingAmount || invoice.outstandingAmount || 0;
      const fieldName = `paymentAmount.${invoice.invoiceId}`;

      if (paymentType === "FULL") {
        setValue(fieldName, Number(outstandingAmount), {
          shouldDirty: true,
          shouldValidate: false,
          shouldTouch: false,
        });
        clearErrors(fieldName);
      }
    });
  }, [paymentTypes, bulkPaymentInvoiceList, setValue, clearErrors]);

const handlePaymentTypeChange = (invoiceId: number, value: string) => {
  const invoice = bulkPaymentInvoiceList?.find(
    (inv) => inv.invoiceId === invoiceId
  );

  if (!invoice) return;

  const outstandingAmount =
    invoice.remainingAmount || invoice.outstandingAmount || 0;

  const fieldName = `paymentAmount.${invoiceId}`;

  const newValue = value === "FULL" ? outstandingAmount : 0;

  setValue(fieldName, newValue, {
    shouldDirty: true,
    shouldValidate: true,
    shouldTouch: true,
  });

  clearErrors(fieldName);
};

  // Define columns for CustomTable
  const invoiceColumns: Column[] = [
    {
      id: "checkBox", label: (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}
        >
          <Controller
            name="selectAllCheckBox"
            control={control}
            render={({ field }) => (
              <Checkbox
                checked={!!field.value}
                onChange={(e) => {
                  field.onChange(e);
                  handleSelectAllCheckBox(e.target.checked);
                }}
                disabled={!bulkPaymentInvoiceList || bulkPaymentInvoiceList.length === 0}
                color={isDark ? "secondary" : "primary"}
                size="small"
                sx={{ p: 0 }}
              />
            )}
          />
        </Box>
      ),
      minWidth: 44,
      padding: "6px 16px",
    },
    {
      id: "mfgInvoiceNumber",
      label: "INV ID",
      minWidth: 100,
      padding: "6px 8px",
    },
    {
      id: "mfgInvoiceDate",
      label: "INV Date",
      minWidth: 95,
      padding: "6px 8px",
    },
    {
      id: "invoiceDueDate",
      label: "INV Due Date",
      minWidth: 100,
      padding: "6px 8px",
    },
    {
      id: "invoiceLateDays",
      label: "INV Aging",
      minWidth: 90,
      padding: "6px 8px",
    },
    {
      id: "outstandingAmount",
      label: "Outstanding Amt",
      minWidth: 110,
      padding: "6px 8px",
    },
    {
      id: "invoiceStatus",
      label: "Status",
      minWidth: 80,
      padding: "6px 8px",
    },
    {
      id: "paymentType",
      label: "Payment Type",
      minWidth: 120,
      padding: "4px 6px",
    },
    {
      id: "paymentAmount",
      label: "Payment Amount",
      minWidth: 130,
      padding: "4px 16px 4px 6px",
    },
  ];

  const onSubmit = async (data: any) => {
    // CS-1682 - Bulk Payment Checkout Implementation
    
    const invoiceLineItemList = activeInvoices.map((invoice: invoiceInfo) => ({
      invoiceId: invoice.invoiceId,
      paymentAmt: Number(paymentAmounts?.[invoice.invoiceId] || 0),
      paymentType: paymentTypes?.[invoice.invoiceId] || "FULL",
    }));

    const requestPayload = {
      paymentMethod: data.paymentMethodType,
      returnPath: location.pathname + location.search,
      invoiceLineItemList: invoiceLineItemList,
    };

    console.log("Bulk payment checkout request:", requestPayload);

    try {
      const response = await dispatch(bulkPaymentCheckoutData(requestPayload));

      if (response && response?.payload) {
        if (response.payload.errors && response.payload.errors.length > 0) {
          const errorMessage = response.payload.errors[0].errorMessage;
          const alertObj: SingleAlertInfo = {
            message: errorMessage,
            alertType: "error",
          };
          dispatch(setSingleAlertObj(alertObj));
        } else if (response.payload.data) {
          const checkoutUrl = response.payload.data.checkoutUrl;
          if (checkoutUrl) {
            window.location.href = checkoutUrl;
          } else {
            const alertObj: SingleAlertInfo = {
              message: "Checkout URL not received. Please try again.",
              alertType: "error",
            };
            dispatch(setSingleAlertObj(alertObj));
          }
        }
      }
    } catch (error) {
      console.error("Bulk payment checkout error:", error);
      const alertObj: SingleAlertInfo = {
        message: "An error occurred while processing the payment. Please try again.",
        alertType: "error",
      };
      dispatch(setSingleAlertObj(alertObj));
    }
  };

  // Handle dialog close with cleanup
  const handleClose = () => {
    reset();
    dispatch(clearBulkPaymentInvoiceList());
    setHasInitialSearchTriggered(false);
    
    if (onResetSelection) {
      onResetSelection();
    }
    
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="bulk-invoice-payment-dialog-title"
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: paperSx(isMobile) }}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
          }
        }}
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          width: "100%",
          maxHeight: isMobile ? "100dvh" : "92vh",
        }}
      >
        <Box sx={headerSx}>
          <Box sx={iconBoxSx(tokens.accent)}>
            <Receipt size={22} color={tokens.accent} strokeWidth={1.8} />
          </Box>
          <Box sx={{ minWidth: 0, pr: 4 }}>
            <Typography
              id="bulk-invoice-payment-dialog-title"
              fontWeight={700}
              sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}
            >
              Bulk Invoice Payment
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
              Process multiple invoice payments in a single transaction
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small" sx={closeIconSx} aria-label="Close">
            <CloseIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>

        <Box
          sx={{
            ...bodySx,
            flex: 1,
            minHeight: 0,
            maxHeight: { xs: "calc(100dvh - 200px)", sm: "min(80vh, calc(92vh - 220px))" },
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            scrollbarGutter: "stable",
            "&::-webkit-scrollbar": { width: 6, height: 6 },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              background: isDark ? "rgba(255,255,255,0.12)" : "#CBD5E1",
              borderRadius: 4,
            },
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography sx={{ mb: 1.5, fontWeight: 700, fontSize: "1rem", color: tokens.textPrimary }}>
                Search Invoices
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  alignItems: "flex-end",
                }}
              >
                {/* CS-1995 - Practice is now selected first; it drives the Product (MFR) dropdown */}
                <Box sx={{ flex: "1 1 200px", minWidth: { xs: "100%", sm: 200 } }}>
                  <Box sx={dropdownWrapWithLabelSx()}>
                    <CustomDropdownField
                      control={control}
                      name="practiceId"
                      label="Practice"
                      options={practiceOptions}
                      disabled={practiceOptions?.length === 1}
                      // CS-1995 - load the dependent Product (MFR) options once per practice selection/change/clear
                      onChangeSelect={(_option: any, value: any) => loadProductsForPractice(value)}
                      size="small"
                    />
                  </Box>
                </Box>
                {/* CS-1995 - Product (MFR) disabled until a practice is selected; options come from the practice-driven fetch */}
                <Box sx={{ flex: "1 1 200px", minWidth: { xs: "100%", sm: 200 } }}>
                  <Box sx={dropdownWrapWithLabelSx()}>
                    <CustomDropdownField
                      control={control}
                      name="productId"
                      label="Product (MFR)"
                      options={productOptionsForPractice}
                      disabled={!selectedPracticeId || productOptionsForPractice.length === 0}
                      size="small"
                    />
                  </Box>
                </Box>
                <Box
                  sx={{
                    flex: "0 0 auto",
                    width: { xs: "100%", sm: "auto" },
                    ml: { xs: 0, sm: "auto" },
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<SearchOutlined sx={{ fontSize: "1rem !important" }} />}
                    onClick={handleSearch}
                    sx={{
                      ...primaryBtnSx,
                      minWidth: 110,
                      minHeight: 35,
                      height: 35,
                      py: 0,
                      px: 1.25,
                      fontSize: "0.8125rem",
                      lineHeight: 1,
                    }}
                  >
                    Search
                  </Button>
                </Box>
              </Box>
              {/* CS-1995 - inform the user when the selected practice has no bulk-payment-eligible products (only after the fetch has resolved for the selected practice, so it never flashes while loading) */}
              {selectedPracticeId && bulkPaymentProductsFetchStatus === "success" && productOptionsForPractice.length === 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography
                    variant="h5"
                    color="warning.main"
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    <InfoOutlined fontSize="small" />
                    No products are currently eligible for bulk payment.
                  </Typography>
                  {/* CS-1995 - clarify what "eligible" means */}
                  <Typography
                    variant="body1"
                    sx={{ fontStyle: "italic", color: tokens.textSecondary, mt: 0.5, ml: 2 }}
                  >
                    Only products with unpaid or partially paid invoices that can be paid through Stripe are shown.
                  </Typography>
                </Box>
              )}
            </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* Invoice Details Table */}
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto 1fr",
                    alignItems: "center",
                    mb: 1.5,
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: tokens.textPrimary }}>
                    Invoice Details
                  </Typography>
                  {bulkPaymentInvoiceList?.length > 0 && (
                    <Box
                      sx={{
                        justifySelf: "center",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 2,
                        py: 0.5,
                        borderRadius: "999px",
                        border: `1px solid ${tokens.accent}`,
                      }}
                    >
                      <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary }}>
                        Payment Total
                      </Typography>
                      <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: tokens.accent }}>
                        {priceFormatter(calculations.totalPaymentAmount)}
                      </Typography>
                    </Box>
                  )}
                  <Box />
                </Box>
                  <Table
                    columns={invoiceColumns}
                    rows={formattedInvoiceRows}
                    disablePagination={true}
                    emptyText="No invoices selected"
                    uniqueValue="Billing"
                    visibleHeader={true}
                    sortableColumns={SORTABLE_INVOICE_COLUMNS}
                    sx={{
                      p: 0,
                      "& .MuiTableContainer-root": {
                        maxHeight: 340,
                        overflowX: "hidden",
                        borderRadius: "12px",
                        border: `1px solid ${tokens.border}`,
                        backgroundColor: tokens.cardBg,
                      },
                      "& .MuiTable-root": {
                        width: "100%",
                      },
                      "& .MuiTableCell-root": {
                        borderColor: tokens.border,
                      },
                      "& .MuiTableCell-body": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                      "& .MuiTableCell-head": {
                        verticalAlign: "middle",
                      },
                      "& .MuiTableCell-head .MuiTypography-root": {
                        whiteSpace: "normal",
                        lineHeight: 1.2,
                        fontSize: "0.78rem",
                      },
                      "& .MuiTableCell-head .MuiIconButton-root": {
                        padding: "2px",
                      },
                      "& .MuiTableCell-head .MuiIconButton-root svg": {
                        fontSize: "14px",
                      },
                      "& .MuiTableCell-head:first-of-type .MuiTypography-root": {
                        display: "flex",
                        alignItems: "center",
                        height: "100%",
                      },
                      "& .MuiTableRow-root:hover td": {
                        backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.03)",
                      },
                    }}
                  />
              </Grid>
              
              {/* Validation Messages - Compact Display */}
              {/* CS-1995 - only show selection/amount validation messages once a search has returned results */}
              {bulkPaymentInvoiceList?.length > 0 && (isAmountExceeded || hasNoActiveInvoices || isTotalAmountTooLow || hasInvalidAmounts || (!hasNoActiveInvoices && calculations.numberOfInvoices === 1)) && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {isAmountExceeded && (
                      <Typography variant="h5" color="error" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <InfoOutlined fontSize="small" />
                        Payment amount ({priceFormatter(calculations.expectedPaymentAmount)}) exceeds Stripe's limit of {priceFormatter(STRIPE_MAX_AMOUNT)}. Please uncheck some invoices.
                      </Typography>
                    )}
                    {isTotalAmountTooLow && (
                      <Typography variant="h5" color="error" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <InfoOutlined fontSize="small" />
                        Total payment amount must be at least $0.50. Please adjust the payment amounts.
                      </Typography>
                    )}
                    {hasNoActiveInvoices && (
                      <Typography variant="h5" color="warning.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <InfoOutlined fontSize="small" />
                        Please select at least two invoice to proceed.
                      </Typography>
                    )}
                    {!hasNoActiveInvoices && calculations.numberOfInvoices === 1 && (
                      <Typography variant="h5" color="warning.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <InfoOutlined fontSize="small" />
                        Bulk payment requires at least 2 invoices. Select one more or use individual payment.
                      </Typography>
                    )}
                  </Box>
                </Grid>
              )}

              {/* Payment Method Section - Only show if stripe config is available */}
              {paymentOptions.length > 0 && (
                <>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography sx={{ mb: 1.5, fontWeight: 700, fontSize: "1rem", color: tokens.textPrimary }}>
                      Pay Method <Box component="span" sx={{ color: tokens.accent }}>*</Box>
                    </Typography>
                    <CustomStyledRadio
                      control={control}
                      controllerName="paymentMethodType"
                      defaultValue={paymentOptions[0]?.value}
                      rules={{ required: "Please select a payment method" }}
                      options={paymentOptions}
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Divider />
              </Grid>


              {/* Payment Summary */}
              <Grid item xs={12}>
                <Typography sx={{ mb: 1.5, fontWeight: 700, fontSize: "1rem", color: tokens.textPrimary }}>
                  Payment Summary
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "12px",
                    backgroundColor: tokens.cardBg,
                    border: `1px solid ${tokens.border}`,
                  }}
                >
                  <Grid container spacing={1.5}>
                    <Grid item xs={6} sm={3}>
                      <Typography sx={{ display: "block", mb: 0.5, fontSize: "0.72rem", color: tokens.textSecondary }}>
                        Number of Invoices
                      </Typography>
                      <Typography sx={{ fontSize: "1.15rem", fontWeight: 700, color: tokens.textPrimary }}>
                        {calculations.numberOfInvoices}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography sx={{ display: "block", mb: 0.5, fontSize: "0.72rem", color: tokens.textSecondary }}>
                        Total Invoice Amount
                      </Typography>
                      <Typography sx={{ fontSize: "1.15rem", fontWeight: 700, color: tokens.textPrimary }}>
                        {priceFormatter(calculations.totalInvoiceAmount)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography sx={{ display: "block", mb: 0.5, fontSize: "0.72rem", color: tokens.textSecondary }}>
                        Total Payment Amount
                      </Typography>
                      <Typography sx={{ fontSize: "1.15rem", fontWeight: 700, color: tokens.textPrimary }}>
                        {priceFormatter(calculations.totalPaymentAmount)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography sx={{ display: "block", mb: 0.5, fontSize: "0.72rem", color: tokens.textSecondary }}>
                        Remaining Balance
                      </Typography>
                      <Typography sx={{ fontSize: "1.15rem", fontWeight: 700, color: tokens.textPrimary }}>
                        {priceFormatter(calculations.remainingBalance)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              {/* Fee Information */}
              {calculations.totalPaymentAmount > 0 && (
                <>
                  <Grid item xs={12}>
                    <Box
                      p={1.5}
                      sx={{
                        borderRadius: "12px",
                        backgroundColor: tokens.cardBg,
                        border: `1px solid ${tokens.border}`,
                      }}
                    >
                      {feeText ? (
                        <Typography variant="body2" sx={{ color: tokens.textPrimary }}>
                          {feeText}
                        </Typography>
                      ) : (
                        <Typography variant="body2" sx={{ color: tokens.textSecondary }}>
                          We are not collecting any additional fees for this payment type as of now.
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: tokens.textPrimary }}>
                        Estimated payment amount (Incl. Stripe processing fees):{" "}
                        <Box
                          component="span"
                          sx={{
                            color: isAmountExceeded ? tokens.error : tokens.accent,
                            fontSize: "1.05rem",
                          }}
                        >
                          {priceFormatter(calculations.expectedPaymentAmount)}
                        </Box>
                      </Typography>
                      <Typography sx={{ fontSize: "0.75rem", color: tokens.textSecondary }}>
                        Note: Ensure your payment method has sufficient credit limit for this transaction.
                      </Typography>
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>
        </Box>

        <Box sx={footerSx}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              flexWrap: "wrap",
              gap: 1.5,
              justifyContent: "flex-end",
              alignItems: { xs: "flex-end", sm: "center" },
            }}
          >
            <Button
              variant="contained"
              type="submit"
              startIcon={<AttachMoney sx={{ fontSize: "1rem !important" }} />}
              disabled={isSubmitDisabled}
              sx={{
                ...primaryBtnSx,
                minWidth: 130,
                ...(isSubmitDisabled && { opacity: 0.5, boxShadow: "none" }),
              }}
            >
              Pay ({calculations.numberOfInvoices} Invoice
              {calculations.numberOfInvoices !== 1 ? "s" : ""})
            </Button>
            <Button
              variant="outlined"
              type="button"
              onClick={handleClose}
              startIcon={<CancelIcon sx={{ fontSize: "1rem !important" }} />}
              sx={{ ...cancelBtnSx, minWidth: 110 }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </form>
    </Dialog>
  );
};

export default BulkInvoicePayment;